import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { LuChevronLeft, LuChevronRight, LuCircleAlert, LuCircleCheck, LuInfo, LuSave } from "react-icons/lu";
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { applyJobPublicApi } from "api/services/jobs";

import { apiError, useJob } from "../api";
import AboutStep from "../apply/AboutStep";
import ApplySuccess from "../apply/ApplySuccess";
import ContactStep from "../apply/ContactStep";
import CvStep from "../apply/CvStep";
import { MobileProgress, Stepper } from "../apply/Progress";
import QuestionsStep from "../apply/QuestionsStep";
import ReviewStep from "../apply/ReviewStep";
import { EMPTY_VALUES, stepsFor } from "../apply/steps";
import useApplyDraft from "../apply/useApplyDraft";
import useEmailVerification from "../apply/useEmailVerification";
import CareersShell, { TopBar } from "../components/CareersShell";
import { isClosed } from "../lib/dates";
import { demoApi } from "../lib/demo";
import { careersPath } from "../lib/paths";
import { toE164 } from "../lib/phone";
import { buildAnswerPayload, questionProblems } from "../lib/questions";
import useDocumentMeta from "../lib/useDocumentMeta";

const ALREADY_APPLIED = /already applied/i;

// The current step lives in the URL (?step=cv), so the browser's Back and
// Forward buttons move between steps and a refresh stays put. A step past
// the furthest one reached falls back to that one.
function useStepParam(steps, maxReached, ready) {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const requested = steps.findIndex((s) => s.id === params.get("step"));
  // No step in the URL ("Continue application"): resume where they got to.
  const wanted = requested === -1 ? maxReached : requested;
  const index = Math.min(wanted, maxReached, steps.length - 1);

  // Keeps any other query parameters (utm_* from a shared link).
  const setStep = useCallback(
    (id, options) =>
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("step", id);
        return next;
      }, options),
    [setParams],
  );

  useEffect(() => {
    if (ready && steps[index].id !== params.get("step")) {
      setStep(steps[index].id, { replace: true, state: location.state });
    }
  }, [ready, index, steps, params, setStep, location.state]);

  const goTo = useCallback(
    (id) => setStep(id, { state: { from: steps[index].id } }),
    [setStep, steps, index],
  );

  const back = useCallback(() => {
    const prev = steps[index - 1];
    if (!prev) return;
    // Came here with Continue: undo that history entry instead of adding one.
    if (location.state?.from === prev.id) navigate(-1);
    else setStep(prev.id, { replace: true });
  }, [steps, index, location.state, navigate, setStep]);

  return { index, step: steps[index], goTo, back };
}

// Fields first, then (on the contact step) the email code.
async function checkStep(target, { form, questions, verification, setShowQuestionErrors }) {
  if (target.id === "questions") {
    const problems = questionProblems(questions, form.getValues("answers") || {});
    const ok = Object.keys(problems).length === 0;
    setShowQuestionErrors(!ok);
    return ok;
  }
  const valid = await form.trigger(target.fields);
  if (target.id !== "contact") return valid;
  const verified = verification.isVerified(form.getValues("email"));
  if (!verified) {
    form.setError("email_verified", { message: "Confirm your email with the code we send you" });
  }
  return valid && verified;
}

async function firstFailingStep(steps, ctx) {
  for (const target of steps) {
    if (target.id !== "review" && !(await checkStep(target, ctx))) return target;
  }
  return null;
}

function toPayload(values, job, questions) {
  return {
    job: job.id,
    first_name: values.first_name.trim(),
    last_name: values.last_name.trim(),
    email: values.email.trim().toLowerCase(),
    phone: toE164(values.phone?.number, values.phone?.country),
    nationality: Number.parseInt(values.nationality, 10),
    residence_status: values.residence_status,
    gender: values.gender,
    resume: values.resume,
    cover_letter: values.cover_letter.trim(),
    answers: buildAnswerPayload(questions, values.answers || {}),
  };
}

// Focus the first control with a problem so keyboard and screen-reader
// users land on it; fall back to scrolling a flagged block into view.
function scrollToFirstError() {
  requestAnimationFrame(() => {
    const control = document.querySelector(
      '.cr-step-card :is(input, select, textarea, button.cr-select-trigger)[aria-invalid="true"]',
    );
    const target = control || document.querySelector('.cr-step-card [data-invalid="true"]');
    target?.scrollIntoView({ behavior: "smooth", block: "center" });
    control?.focus({ preventScroll: true });
  });
}

function RestoredBanner({ draft, onStartOver }) {
  if (!draft.restoredAt) return null;
  return (
    <div className="cr-banner" role="status">
      <LuSave aria-hidden="true" />
      <div className="cr-banner-body">
        <strong>Welcome back.</strong> We kept everything you entered on this device.
        {draft.emailExpired && " Please confirm your email again before you continue."}
      </div>
      <button type="button" className="cr-link-btn" onClick={onStartOver}>
        Start again
      </button>
    </div>
  );
}

function SubmitError({ message }) {
  if (!message) return null;
  return (
    <div className="cr-banner cr-banner--error" role="alert">
      <LuCircleAlert aria-hidden="true" />
      <div className="cr-banner-body">{message}</div>
    </div>
  );
}

function StepActions({ isFirst, isLast, sending, savedAt, roleHref, onBack, onNext }) {
  let nextLabel = "Continue";
  if (isLast) nextLabel = sending ? "Sending…" : "Send application";
  return (
    <div className="cr-step-actions">
      <span className={`cr-saved ${savedAt ? "cr-saved--on" : ""}`} aria-live="polite">
        {savedAt ? <LuCircleCheck aria-hidden="true" /> : <LuInfo aria-hidden="true" />}
        {savedAt ? "Saved on this device" : "Your answers save as you go"}
      </span>
      <div className="cr-step-buttons">
        {isFirst ? (
          <Link to={roleHref} className="cr-btn cr-btn--secondary">
            <LuChevronLeft aria-hidden="true" /> Back to role
          </Link>
        ) : (
          <button type="button" className="cr-btn cr-btn--secondary" onClick={onBack} disabled={sending}>
            <LuChevronLeft aria-hidden="true" /> Back
          </button>
        )}
        <button type="button" className="cr-btn cr-btn--primary" onClick={onNext} disabled={sending}>
          {sending && <span className="cr-spinner" aria-hidden="true" />}
          {nextLabel}
          {!isLast && !sending && <LuChevronRight aria-hidden="true" />}
        </button>
      </div>
    </div>
  );
}

function StepBody({ step, questions, verification, showQuestionErrors, onEdit }) {
  switch (step.id) {
    case "contact":
      return <ContactStep verification={verification} />;
    case "about":
      return <AboutStep />;
    case "cv":
      return <CvStep />;
    case "questions":
      return <QuestionsStep questions={questions} showErrors={showQuestionErrors} />;
    default:
      return <ReviewStep questions={questions} onEdit={onEdit} />;
  }
}

function useSubmit(job, questions, draft) {
  const [state, setState] = useState({ sending: false, error: "", sent: null });

  const submit = async (values) => {
    setState({ sending: true, error: "", sent: null });
    try {
      const apply = job.demo ? demoApi.apply : applyJobPublicApi;
      const { data } = await apply(toPayload(values, job, questions));
      draft.discard();
      setState({
        sending: false,
        error: "",
        sent: {
          email: values.email.trim(),
          reference: data?.data?.application_reference ?? data?.application_reference,
        },
      });
    } catch (error) {
      const message = apiError(error, "");
      setState({
        sending: false,
        sent: null,
        error: ALREADY_APPLIED.test(message)
          ? `You've already applied for this role with ${values.email.trim()}. We have your application.`
          : message || "Your application didn't send. Check your connection and try again.",
      });
    }
  };

  return { ...state, submit };
}

function ApplyFlow({ job, slug, onSent }) {
  const form = useForm({ defaultValues: EMPTY_VALUES, mode: "onTouched" });
  const draft = useApplyDraft(slug, form, { demo: Boolean(job.demo) });
  const verification = useEmailVerification({
    jobId: job.id,
    demo: Boolean(job.demo),
    initialVerifiedEmail: draft.verifiedEmail,
    onVerified: useCallback(
      (email) => {
        form.clearErrors("email_verified");
        draft.rememberVerified(email);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [draft.rememberVerified],
    ),
  });
  const questions = job.questions || [];
  const steps = useMemo(() => stepsFor(questions.length > 0), [questions.length]);
  const { index, step, goTo, back } = useStepParam(steps, draft.maxReached, draft.ready);
  const [showQuestionErrors, setShowQuestionErrors] = useState(false);
  const submission = useSubmit(job, questions, draft);
  const headingRef = useRef(null);
  const firstRender = useRef(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    window.scrollTo({ top: 0 });
    headingRef.current?.focus({ preventScroll: true });
  }, [step.id]);

  const ctx = { form, questions, verification, setShowQuestionErrors };

  // The review step has no fields of its own, so sending re-checks every
  // step (a restored draft may hold an email whose confirmation expired) and
  // opens the first one that needs attention.
  const validateStep = async () => {
    if (step.id !== "review") return checkStep(step, ctx);
    const failing = await firstFailingStep(steps, ctx);
    if (failing) goTo(failing.id);
    return !failing;
  };

  const startOver = () => {
    verification.reset();
    draft.startOver();
  };

  const next = async () => {
    if (!(await validateStep())) {
      scrollToFirstError();
      return;
    }
    if (index === steps.length - 1) {
      submission.submit(form.getValues());
      return;
    }
    draft.reach(index + 1);
    draft.dismissRestored();
    goTo(steps[index + 1].id);
  };

  useEffect(() => {
    if (submission.sent) onSent();
  }, [submission.sent, onSent]);

  if (submission.sent) {
    return <ApplySuccess job={job} email={submission.sent.email} reference={submission.sent.reference} />;
  }

  const roleHref = careersPath(slug);
  const goBack = () => (index === 0 ? navigate(roleHref) : back());

  return (
    <FormProvider {...form}>
      <div className="cr-apply-layout">
        <Stepper job={job} steps={steps} index={index} maxReached={draft.maxReached} onGo={goTo} />
        <div className="cr-apply-main">
          <MobileProgress
            steps={steps}
            index={index}
            onBack={goBack}
            backLabel={index === 0 ? "Role details" : "Back"}
          />
          <div className="cr-step-head">
            <h1 className="cr-h1" ref={headingRef} tabIndex={-1}>
              {step.title}
            </h1>
            <p>{step.intro}</p>
          </div>
          <RestoredBanner draft={draft} onStartOver={startOver} />
          <form
            className="cr-step-card"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              next();
            }}
          >
            <StepBody
              step={step}
              questions={questions}
              verification={verification}
              showQuestionErrors={showQuestionErrors}
              onEdit={goTo}
            />
            <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
          </form>
          {step.id === "review" && <SubmitError message={submission.error} />}
          <StepActions
            isFirst={index === 0}
            isLast={index === steps.length - 1}
            sending={submission.sending}
            savedAt={draft.savedAt}
            roleHref={roleHref}
            onBack={back}
            onNext={next}
          />
        </div>
      </div>
    </FormProvider>
  );
}

function ApplySkeleton() {
  return (
    <div className="cr-apply-layout" aria-busy="true">
      <div className="cr-apply-side">
        <span className="cr-skeleton" style={{ height: 14, width: 160 }} />
        <span className="cr-skeleton" style={{ height: 160, marginTop: 20, borderRadius: 12 }} />
      </div>
      <div>
        <span className="cr-skeleton" style={{ height: 28, width: "45%" }} />
        <span className="cr-skeleton" style={{ height: 300, marginTop: 20, borderRadius: 16 }} />
      </div>
      <span className="cr-visually-hidden">Loading application form</span>
    </div>
  );
}

export default function ApplyPage() {
  const { slug } = useParams();
  const { data: job, status, refetch } = useJob(slug);
  const [sent, setSent] = useState(false);
  const markSent = useCallback(() => setSent(true), []);
  useDocumentMeta({ title: job ? `Apply: ${job.title} – Nando's UAE` : "Apply – Nando's UAE" });

  if (status === "success" && (!job || isClosed(job.expiry_date))) {
    return <Navigate to={careersPath(slug)} replace />;
  }
  // An old link by numeric id (/job-apply/6) settles on the role's own URL.
  if (job?.slug && job.slug !== slug) {
    return <Navigate to={`${careersPath(job.slug, "apply")}${window.location.search}`} replace />;
  }

  return (
    <CareersShell className="cr-apply">
      <TopBar title={job ? `Applying for ${job.title}` : null}>
        {sent ? (
          <Link to={careersPath()} className="cr-navlink">
            All roles
          </Link>
        ) : (
          <Link to={careersPath(slug)} className="cr-navlink">
            <LuSave aria-hidden="true" /> Save and exit
          </Link>
        )}
      </TopBar>
      <main id="cr-main" className="cr-main">
        <div className="cr-wrap">
          {status === "pending" && <ApplySkeleton />}
          {status === "error" && (
            <div className="cr-state" role="alert" style={{ marginTop: 32 }}>
              <h1 className="cr-h2">We couldn't load the form</h1>
              <p className="cr-muted">Check your connection and try again.</p>
              <button type="button" className="cr-btn cr-btn--primary" onClick={() => refetch()}>
                Try again
              </button>
            </div>
          )}
          {status === "success" && job && <ApplyFlow job={job} slug={slug} onSent={markSent} />}
        </div>
      </main>
    </CareersShell>
  );
}
